import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          role: 'job_seeker' | 'employer' | 'admin';
          full_name: string;
          email: string;
          avatar_url: string | null;
          company_name: string | null;
          bio: string | null;
          location: string | null;
          phone: string | null;
          phone_number: string | null;
          phone_verified: boolean;
          phone_verification_code: string | null;
          phone_verification_expires_at: string | null;
          job_title: string | null;
          experience_level: 'entry' | 'mid' | 'senior' | 'executive' | null;
          remote_preference: boolean;
          desired_job_types: string[] | null;
          skills: string[] | null;
          min_salary: number | null;
          max_salary: number | null;
          account_complete: boolean;
          onboarding_completed_at: string | null;
          onboarding_skipped: boolean;
          referral_source: string | null;
          referred_by: string | null;
          referral_code: string | null;
          referral_code_used: string | null;
          connection_reason: string | null;
          connection_reason_other: string | null;
          website: string | null;
          headquarters: string | null;
          industry: string | null;
          company_size: string | null;
          founded_year: string | null;
          benefits: string[] | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['profiles']['Row'], 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['profiles']['Insert']>;
      };
      jobs: {
        Row: {
          id: string;
          employer_id: string;
          title: string;
          description: string;
          company: string;
          location: string;
          remote: boolean;
          salary_min: number | null;
          salary_max: number | null;
          experience_level: 'entry' | 'mid' | 'senior' | 'executive';
          job_type: 'full_time' | 'part_time' | 'contract' | 'internship';
          skills: string[];
          status: 'active' | 'paused' | 'closed';
          views_count: number;
          created_at: string;
          updated_at: string;
        };
      };
      resumes: {
        Row: {
          id: string;
          user_id: string;
          title: string;
          content: Record<string, any>;
          encryption_metadata: Record<string, any>;
          file_url: string | null;
          is_primary: boolean;
          created_at: string;
          updated_at: string;
        };
      };
      applications: {
        Row: {
          id: string;
          job_id: string;
          applicant_id: string;
          resume_id: string;
          status: 'pending' | 'reviewed' | 'interview' | 'accepted' | 'rejected';
          cover_letter: string | null;
          match_score: number | null;
          employer_notes: string | null;
          created_at: string;
          updated_at: string;
        };
      };
      swipes: {
        Row: {
          id: string;
          user_id: string;
          job_id: string;
          direction: 'left' | 'right';
          created_at: string;
        };
      };
      messages: {
        Row: {
          id: string;
          application_id: string;
          sender_id: string;
          content: string;
          encryption_metadata: Record<string, any>;
          read: boolean;
          created_at: string;
        };
      };
      credits: {
        Row: {
          id: string;
          user_id: string;
          balance: number;
          total_earned: number;
          total_spent: number;
          created_at: string;
          updated_at: string;
        };
      };
      credit_transactions: {
        Row: {
          id: string;
          user_id: string;
          amount: number;
          type: string;
          description: string;
          created_at: string;
        };
      };
    };
  };
};
